/**
 * Reading `api/leaderboards.json` (P9) — the one thing this client fetches that is
 * not the API.
 *
 * It is a static object written to a bucket by `quest/jobs/leaderboard.py` when a character
 * dies, served by the site's own CloudFront distribution under `/api/*`. So it is
 * same-origin, unauthenticated, cacheable, and **not in `openapi.json`** — which is why the
 * types below are hand-written where every other type in this app is generated. The job's
 * module docstring calls its shape "the contract"; this file is the other half of it, and
 * `leaderboard.test.ts` pins the two together.
 *
 * ## The 200 that means 404
 *
 * `infrastructure/quest-v2-fe/README.md` records a deliberate edge: `custom_error_response` on the
 * distribution is **distribution-wide**, so a missing `/api/leaderboards.json` does not 404 —
 * it answers `index.html` with a **200**. That is not a bug to work around at the edge (the
 * alternatives are a CloudFront Function or a second distribution, both worse), and that file is
 * explicit that the leaderboard client must treat a non-JSON body as "no leaderboard yet"
 * rather than trusting the status.
 *
 * This is that check, and it is the whole reason `fetchLeaderboard` returns `null` rather
 * than throwing on a body it cannot read. Today it always fires: **no leaderboard has ever
 * been written.** The job needs a real death in a real story, so the deployed site's first
 * visit to this route is the empty state, and the empty state is therefore the one that had
 * to be right first.
 *
 * A network failure is a different thing from a missing file and still throws — the query
 * renders an error, and "we could not reach the server" must not read as "nobody has died".
 */
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '@/api/client';

export interface LeaderboardEntry {
  character_name: string;
  chapter_count: number;
  /** Null on a run the job included before it finished — render the gap, do not hide it. */
  cause_of_death: string | null;
  completed_at: string | null;
  /**
   * The language the *story* was played in, which is the language `cause_of_death` and
   * `character_name` are written in — not the reader's (L4). One global file means one
   * board carrying all three at once, so this is what lets `Leaderboard.tsx` tag a
   * foreign-language epitaph and hand it to a screen reader with the right `lang`.
   */
  locale: string;
}

export interface LeaderboardFile {
  generated_at: string;
  /** Most recently dead. */
  recent: LeaderboardEntry[];
  /** Longest-surviving, by chapter count. */
  longest: LeaderboardEntry[];
}

/** Same-origin by construction: no base URL, no CORS, no bearer. */
export const LEADERBOARD_URL = '/api/leaderboards.json';

/** Five minutes, matching the object's own `Cache-Control: public, max-age=300`. */
export const LEADERBOARD_STALE_MS = 300_000;

/**
 * `null` for "there is no leaderboard yet", in all three of the ways that can look:
 * a 404 (a dev server, or S3 without the SPA fallback), a 200 carrying `index.html`
 * (CloudFront), and a 200 carrying JSON that is not this shape.
 */
export function parseLeaderboard(body: string): LeaderboardFile | null {
  let payload: unknown;
  try {
    payload = JSON.parse(body);
  } catch {
    return null;
  }

  if (typeof payload !== 'object' || payload === null) return null;
  const { generated_at, recent, longest } = payload as Record<string, unknown>;
  if (typeof generated_at !== 'string') return null;
  if (!Array.isArray(recent) || !Array.isArray(longest)) return null;

  return {
    generated_at,
    recent: recent.filter(isEntry),
    longest: longest.filter(isEntry),
  };
}

/**
 * The three fields the job always writes. `cause_of_death` and `completed_at` are nullable in
 * its own model and are checked nowhere, because a row with a gap in either still renders —
 * `locale` is required for the opposite reason: it is what decides *how* the row renders, and
 * a row claiming a `locale` it does not carry would reach `localeLabel` as `undefined`. No
 * default is coined here (there is no such thing as a story with no language); the file has
 * never been written in production, so there is no old shape to be lenient about either.
 */
function isEntry(value: unknown): value is LeaderboardEntry {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.character_name === 'string' &&
    typeof entry.chapter_count === 'number' &&
    typeof entry.locale === 'string'
  );
}

export async function fetchLeaderboard(signal?: AbortSignal): Promise<LeaderboardFile | null> {
  let response: Response;
  try {
    response = await fetch(LEADERBOARD_URL, { signal, headers: { Accept: 'application/json' } });
  } catch (cause) {
    if (signal?.aborted) throw cause;
    throw new ApiError('network', 'The leaderboard could not be fetched.', { status: 0 });
  }

  if (response.status === 404) return null;
  if (!response.ok) {
    throw new ApiError('internal', `${String(response.status)} fetching the leaderboard.`, {
      status: response.status,
    });
  }

  return parseLeaderboard(await response.text());
}

export function useLeaderboard() {
  return useQuery({
    queryKey: ['leaderboard'],
    staleTime: LEADERBOARD_STALE_MS,
    queryFn: ({ signal }) => fetchLeaderboard(signal),
  });
}
